import React, { useEffect, useState } from "react";
import { Animated, Modal, FlatList, ScrollView, StyleSheet, View, TouchableOpacity, TouchableWithoutFeedback, useWindowDimensions, Linking, Platform } from "react-native";
import { Box, HStack, Text, Pressable, VStack, Icon } from "native-base";
import { Ionicons } from '@expo/vector-icons';
import DashedLine from "react-native-dashed-line";
import { useNavigation } from "@react-navigation/native";
import { CateMenuAndOptionData, CateMenuListData, MenuListData, ShopTblData } from "./lib/model";
import { buildSections, MenuSection, columnsFor, flattenWithOffsets, sectionIndexForOffset, FlatEntry, FlatLayout } from "./lib/menuLayout";
import { pickDayHours, formatTime12, DayHours } from "./lib/hours";
import apps, { Happyhour, SaleMode } from "./lib/apps";
import * as remote from "./lib/remote";
import { useQuery } from "@tanstack/react-query";
import { LoadingIndicator } from "./component/loading";
import { fixPrice, money, alcoholWarningUri } from "./lib/utils";
import ThumbImage from "./component/ThumbImage";
import { useScrollMore, ScrollMoreHint } from "./component/ScrollMore";
import { circleBtnAccent, safeAccent } from "./component/BackButton";
import { useRecoilValue, useRecoilState } from "recoil";
import { cartState } from "./state/cartState";
import { orderState } from "./state/orderState";
import { PALETTE } from "./lib/palette";
import { renderMenuCard, AlcoholWarning, ROW_GAP, INFO_H, ROW_BASE, cardsThumbHFor } from "./component/MenuCard";

// 긴 상점명이 두 줄로 깨지지 않도록 한 줄에 맞게 폰트를 자동 축소(+정 넘치면 말줄임).
// react-native-web는 adjustsFontSizeToFit 미지원 + native-base Text onLayout 측정이 불안정해,
// 신뢰성 높은 컨테이너 폭(View onLayout)과 글자수 기반 추정으로 폰트 크기를 계산한다.
function ShopName({ label }: { label: string }) {
    const BASE = 22, MIN = 9;
    const EM = 0.66; // Bold sans 평균 글자폭(em). 보수적으로 잡아 더 적극적으로 축소(오버플로=말줄임 방지).
    const [avail, setAvail] = useState<number | null>(null);

    const len = Math.max(label.length, 1);
    let fontSize = BASE;
    if (avail && len * EM * BASE > avail) {
        fontSize = Math.max(MIN, Math.min(BASE, Math.floor(avail / (len * EM))));
    }

    return (
        <View style={{ flex: 1, marginHorizontal: 12, alignItems: "center", justifyContent: "center" }}
              onLayout={e => setAvail(e.nativeEvent.layout.width)}>
            <Text style={[styles.shopName, { fontSize, textAlign: "center" }]} numberOfLines={1} ellipsizeMode="tail">
                {label}
            </Text>
        </View>
    );
}

// B6a: 요일별 영업시간. 1줄=영업시간(off=Closed, 미설정=Open), 2줄=break(없으면 빈 줄).
// 두 줄을 항상 렌더해 break 유무와 무관하게 행 높이를 동일하게 유지.
const WEEKDAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
function dayHoursLine(d: DayHours): string {
    if (d.off) return "Closed";
    const o = formatTime12(d.open), c = formatTime12(d.close);
    return (!o || !c) ? "Open" : `${o} – ${c}`;
}
function dayBreakLine(d: DayHours): string {
    const bs = formatTime12(d.breakStart), be = formatTime12(d.breakEnd);
    return (bs && be) ? `Break ${bs} – ${be}` : "";
}

// B6a: 헤더 우측 햄버거로 여는 오른쪽 슬라이드 Store Info 시트(표시 전용).
// 매장명·주소·요일별 영업시간(월→일). Call Staff 항목은 Track 3에서 추가.
function StoreInfoModal({ shop, visible, onClose }: { shop: ShopTblData; visible: boolean; onClose: () => void }) {
    const PANEL_W = 320;
    const slide = React.useRef(new Animated.Value(PANEL_W)).current; // off-screen right
    React.useEffect(() => {
        Animated.timing(slide, { toValue: visible ? 0 : PANEL_W, duration: 220, useNativeDriver: false }).start();
    }, [visible, slide]);

    // Call Staff(다인인 QR 전용). 게이트: 매장 활성화 && qrType TB* && Menu 보기 아님. 스팸 방지 45s 쿨다운.
    // Menu 보기(tableNo === "menu")는 TB* qrType이지만 실제 테이블이 없으므로 Call Staff 제외(Takeaway와 동일 취급).
    const isMenuOnly = (apps.tableNo ?? "").toLowerCase() === "menu";
    const canCallStaff = shop.callStaff === true && (apps.qrType?.startsWith("TB") ?? false) && !isMenuOnly;
    const [callState, setCallState] = useState<"idle" | "confirm" | "sending" | "sent">("idle");
    // Operating Hours 기본 표시: Call Staff 켜짐 → 오늘만(버튼 가림 방지), 꺼짐 → 전부. 헤더 탭으로 토글 가능.
    const [hoursOpen, setHoursOpen] = useState(!canCallStaff);
    const todayDow = new Date().getDay();
    useEffect(() => {
        if (callState === "sent") {
            const t = setTimeout(() => setCallState("idle"), 45000);
            return () => clearTimeout(t);
        }
    }, [callState]);
    const doCallStaff = async () => {
        setCallState("sending");
        try {
            const res = await remote.staffCall(apps.shopId, apps.tableNo, apps.qrType);
            setCallState(res && res.ok ? "sent" : "idle");
        } catch (e) {
            setCallState("idle");
        }
    };

    return (
        <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
            <TouchableWithoutFeedback onPress={onClose}>
                <View style={styles.storeBackdrop} />
            </TouchableWithoutFeedback>
            <Animated.View style={[styles.storePanel, { width: PANEL_W, transform: [{ translateX: slide }] }]}>
                <View style={styles.storeHead}>
                    <Text style={styles.storeTitle}>Store Info</Text>
                    <TouchableOpacity onPress={onClose} style={circleBtnAccent(safeAccent())} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                        <Text style={styles.storeCloseGlyph}>✕</Text>
                    </TouchableOpacity>
                </View>
                <ScrollView contentContainerStyle={{ padding: 16 }} showsVerticalScrollIndicator={false}>
                    <Text style={styles.storeName}>{shop.name}</Text>
                    {(() => {
                        // 주소 뒤에 state(NSW/QLD…) 부기. 서버 미배포 시 state undefined → 주소만.
                        // 주소가 있으면 그 아래에 주소 기반 지도(웹 임베드, 키 불필요) + 길찾기 버튼.
                        const addr = [shop.address, shop.state].filter(Boolean).join(", ");
                        if (!addr) return null;
                        const q = encodeURIComponent(addr);
                        const accent = apps.getAccent();
                        return (
                            <>
                                <Text style={styles.storeAddr}>{addr}</Text>
                                {Platform.OS === "web" && (
                                    <View style={styles.mapWrap}>
                                        {/* 웹 전용: Google 지도 임베드(output=embed, API 키 불필요). checkout iframe과 동일 패턴. */}
                                        <iframe
                                            title="Store location"
                                            src={`https://maps.google.com/maps?q=${q}&z=16&output=embed`}
                                            style={{ width: "100%", height: 160, border: 0, display: "block" }}
                                            loading="lazy"
                                        />
                                    </View>
                                )}
                                <TouchableOpacity
                                    style={[styles.directionsBtn, { borderColor: accent }]}
                                    onPress={() => Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${q}`)}
                                >
                                    <Text style={[styles.directionsBtnText, { color: accent }]}>Get Directions</Text>
                                </TouchableOpacity>
                            </>
                        );
                    })()}
                    {/* Operating Hours: 헤더를 탭하면 오늘만 ↔ 전체 토글(Call Staff가 아래에 보이도록 기본 접힘) */}
                    <TouchableOpacity onPress={() => setHoursOpen(o => !o)} activeOpacity={0.7} style={styles.storeHoursHeader}>
                        <Text style={[styles.storeSectionLabel, styles.storeHoursHeaderLabel]}>Operating Hours</Text>
                        <HStack style={{ alignItems: "center" }}>
                            {!hoursOpen ? <Text style={styles.storeHoursHint}>Today</Text> : null}
                            <Icon as={Ionicons} name={hoursOpen ? "chevron-up" : "chevron-down"} size="sm" color={PALETTE.MUTED} />
                        </HStack>
                    </TouchableOpacity>
                    {(hoursOpen ? [1, 2, 3, 4, 5, 6, 0] : [todayDow]).map((dow) => {
                        const d = pickDayHours(shop, dow);
                        const brk = dayBreakLine(d);
                        const isToday = dow === todayDow;
                        return (
                            <HStack key={dow} style={styles.storeHoursRow}>
                                <Text style={[styles.storeDay, isToday && styles.storeDayToday]}>{WEEKDAY_FULL[dow]}</Text>
                                <View style={styles.storeHoursCol}>
                                    <Text style={[styles.storeHoursText, isToday && styles.storeHoursTextToday]} numberOfLines={1}>{dayHoursLine(d)}</Text>
                                    {/* break는 있을 때만 렌더. 행 높이를 minHeight로 2줄분 고정 + 세로 중앙정렬이라
                                        break 유무와 무관하게 요일·시간이 항상 가운데에 온다. */}
                                    {brk ? <Text style={styles.storeBreakText} numberOfLines={1}>{brk}</Text> : null}
                                </View>
                            </HStack>
                        );
                    })}
                    {canCallStaff && (
                        <View style={{ marginTop: 4 }}>
                            <Text style={styles.storeSectionLabel}>Assistance</Text>
                            {callState === "sent" ? (
                                <View style={[styles.callStaffBtn, { backgroundColor: apps.accentTint(), borderColor: apps.getAccent(), borderWidth: 1 }]}>
                                    <Text style={[styles.callStaffBtnText, { color: apps.getAccent() }]}>A staff member is on the way</Text>
                                </View>
                            ) : callState === "confirm" ? (
                                <View>
                                    <Text style={styles.callStaffPrompt}>Call a staff member?</Text>
                                    <HStack style={{ marginTop: 10 }} space={2}>
                                        <TouchableOpacity style={[styles.callStaffBtn, styles.callStaffCancel, { flex: 1 }]} onPress={() => setCallState("idle")}>
                                            <Text style={[styles.callStaffBtnText, { color: PALETTE.TEXT }]}>Cancel</Text>
                                        </TouchableOpacity>
                                        <TouchableOpacity style={[styles.callStaffBtn, { flex: 1, backgroundColor: apps.getAccent() }]} onPress={doCallStaff}>
                                            <Text style={[styles.callStaffBtnText, { color: apps.onAccent() }]}>Call</Text>
                                        </TouchableOpacity>
                                    </HStack>
                                </View>
                            ) : (
                                <TouchableOpacity
                                    style={[styles.callStaffBtn, { backgroundColor: apps.getAccent(), opacity: callState === "sending" ? 0.6 : 1 }]}
                                    disabled={callState === "sending"}
                                    onPress={() => setCallState("confirm")}
                                >
                                    <Text style={[styles.callStaffBtnText, { color: apps.onAccent() }]}>
                                        {callState === "sending" ? "Calling…" : "Call Staff"}
                                    </Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    )}
                </ScrollView>
            </Animated.View>
        </Modal>
    );
}

function TitleBar({ shop }: { shop: ShopTblData }) {
    const [showInfo, setShowInfo] = useState(false);

    return (
        <>
            <HStack style={styles.titleContainer}>
                {/* 좌측 스페이서: 우측 햄버거(26)와 대칭을 맞춰 ShopName을 정확히 중앙에 둔다. */}
                <View style={{ width: 26 }} />
                <ShopName label={`${shop.name}${apps.isProd() == false ? " [Dev]" : ""}`} />
                <Pressable onPress={() => setShowInfo(true)} accessibilityLabel="Store info menu">
                    <Icon as={Ionicons} name="information-circle-outline" size={7} color={PALETTE.TEXT} />
                </Pressable>
            </HStack>
            <StoreInfoModal shop={shop} visible={showInfo} onClose={() => setShowInfo(false)} />
        </>
    );
}

function CategoryTabs({
    sections,
    activeIndex,
    onTap,
    tabsRef,
}: {
    sections: MenuSection[];
    activeIndex: number;
    onTap: (i: number) => void;
    tabsRef: React.RefObject<FlatList<MenuSection>>;
}) {
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    return (
        <FlatList
            ref={tabsRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            data={sections}
            keyExtractor={(s) => String(s.cateId)}
            onScrollToIndexFailed={() => {}}
            style={styles.tabsStrip}
            contentContainerStyle={styles.tabsContent}
            renderItem={({ item, index }) => {
                const selected = index === activeIndex;
                return (
                    <TouchableOpacity
                        onPress={() => onTap(index)}
                        style={[styles.chip, { backgroundColor: selected ? accent : PALETTE.CHIP_BG }]}
                    >
                        <Text style={[styles.chipText, { color: selected ? onAccent : PALETTE.MUTED }]}>
                            {item.cateName}
                        </Text>
                    </TouchableOpacity>
                );
            }}
        />
    );
}

// Desktop 3-pane (B7): vertical category sidebar. Reuses the same
// activeIndex/onTap as the horizontal CategoryTabs — clicking an item calls the
// existing onTapTab(i) so B4/B5 scroll logic is untouched.
function CategorySidebar({
    sections,
    activeIndex,
    onTap,
}: {
    sections: MenuSection[];
    activeIndex: number;
    onTap: (i: number) => void;
}) {
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    return (
        <ScrollView style={styles.sidebar} contentContainerStyle={styles.sidebarContent} showsVerticalScrollIndicator={false}>
            <Text style={styles.sidebarTitle}>Categories</Text>
            {sections.map((s, index) => {
                const selected = index === activeIndex;
                return (
                    <TouchableOpacity
                        key={String(s.cateId)}
                        onPress={() => onTap(index)}
                        style={[styles.sidebarItem, selected && { backgroundColor: accent }]}
                    >
                        <Text
                            style={[styles.sidebarItemText, selected ? { color: onAccent } : styles.sidebarItemTextUnselected]}
                            numberOfLines={2}
                        >
                            {s.cateName}
                        </Text>
                    </TouchableOpacity>
                );
            })}
        </ScrollView>
    );
}

// B4 (#5): mobile/tablet "all categories" left slide-in panel. Lists every section;
// tapping one reuses the EXISTING onTapTab(i) (so tap-scroll/scroll-spy logic is
// untouched) then closes. Left-slide via Animated translateX. Web-safe: useNativeDriver
// false (RNW has no native animated module). Only rendered in the non-wide layout.
function AllCategoriesPanel({ sections, activeIndex, onTapTab, visible, onClose }: {
    sections: MenuSection[];
    activeIndex: number;
    onTapTab: (i: number) => void;
    visible: boolean;
    onClose: () => void;
}) {
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    const PANEL_W = 300;
    const slide = React.useRef(new Animated.Value(-PANEL_W)).current; // off-screen left

    React.useEffect(() => {
        Animated.timing(slide, {
            toValue: visible ? 0 : -PANEL_W,
            duration: 220,
            useNativeDriver: false,
        }).start();
    }, [visible, slide]);

    return (
        <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
            <TouchableWithoutFeedback onPress={onClose}>
                <View style={styles.allCatBackdrop} />
            </TouchableWithoutFeedback>
            <Animated.View style={[styles.allCatPanel, { width: PANEL_W, transform: [{ translateX: slide }] }]}>
                <View style={styles.allCatHead}>
                    <Text style={styles.allCatTitle}>All Categories</Text>
                    <TouchableOpacity onPress={onClose} style={circleBtnAccent(safeAccent())} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                        <Text style={styles.allCatCloseGlyph}>✕</Text>
                    </TouchableOpacity>
                </View>
                <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 16 }}>
                    {sections.map((s, index) => {
                        const selected = index === activeIndex;
                        return (
                            <TouchableOpacity
                                key={String(s.cateId)}
                                onPress={() => { onTapTab(index); onClose(); }}
                                style={[styles.allCatItem, selected && { backgroundColor: accent }]}
                            >
                                <Text
                                    style={[styles.allCatItemText, selected ? { color: onAccent } : styles.allCatItemTextUnselected]}
                                    numberOfLines={2}
                                >
                                    {s.cateName}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            </Animated.View>
        </Modal>
    );
}

// Desktop 3-pane (B7): persistent right cart panel. Reads cartState directly and
// hands off to the EXISTING order flow via navigation.navigate("Cart") — same as
// the mobile FAB (ViewCartPopup). Does not rebuild cart/checkout.
function CartPanel() {
    const [cart, setCart] = useRecoilState(cartState);
    const navigation = useNavigation<any>();
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    const isEmpty = cart.items.length === 0;

    // 카트 항목의 선택 옵션 요약(모바일 카트 optionsDescr와 동일 규칙) — 어떤 항목인지 식별용.
    const optionsOf = (it: any): string =>
        Array.from(it.options.values()).flat().map((oi: any) => oi.name).join(", ");

    // 행 탭 → 상세화면(Menu)으로 selectedTag 전달 → 수량·옵션 프리필 후 수정(모바일 카트와 동일).
    const editItem = (tag: string) => navigation.navigate("Menu", { selectedTag: tag });
    // 🗑 → 해당 항목 삭제(모바일 카트와 동일).
    const removeItem = (tag: string) => setCart(cart.removeItem(tag));
    const { showHint, scrollToEnd, scrollProps } = useScrollMore();   // 스크롤 "더 있음" 힌트

    return (
        <View style={styles.cartPanel}>
            <Box style={styles.cartPanelHead}>
                <Text style={styles.cartPanelTitle}>Your Order</Text>
                {!isEmpty ? (
                    <Text style={styles.cartPanelHint}>Tap an item to edit · 🗑 to remove</Text>
                ) : null}
            </Box>
            {isEmpty ? (
                <View style={styles.cartPanelEmpty}>
                    <Text style={styles.cartPanelEmptyText}>Your cart is empty</Text>
                </View>
            ) : (
                <View style={{flex: 1}}>
                <ScrollView {...scrollProps} style={styles.cartPanelItems}>
                    {cart.items.map((it, idx) => (
                        <React.Fragment key={it.tag()}>
                            <HStack style={styles.cartRow}>
                                <TouchableOpacity style={styles.cartRowTap} onPress={() => editItem(it.tag())}>
                                    <HStack flex={1} alignItems="flex-start">
                                        <ThumbImage shopid={apps.currentShop?.shopid ?? ""} menuid={it.menu.menuid} imageVer={it.menu.imageVer} style={styles.cartRowThumb} />
                                        <VStack flex={1} style={styles.cartRowMain}>
                                            <Text style={styles.cartRowName} numberOfLines={2}>{it.menu.name}</Text>
                                            {optionsOf(it) ? (
                                                <Text style={styles.cartRowOptions} numberOfLines={2}>{optionsOf(it)}</Text>
                                            ) : null}
                                            {it.note ? (
                                                <Text style={styles.cartRowNote} numberOfLines={2}>📝 {it.note}</Text>
                                            ) : null}
                                        </VStack>
                                        {/* 금액 아래 ×수량 칩(다른 화면과 동일) — 메뉴명 공간 확보 */}
                                        <VStack style={styles.cartRowRight}>
                                            <Text style={styles.cartRowPrice}>{money(it.itemPrice * it.qty)}</Text>
                                            <View style={styles.cartRowQtyChip}><Text style={styles.cartRowQtyChipText}>×{it.qty}</Text></View>
                                        </VStack>
                                    </HStack>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={() => removeItem(it.tag())} style={styles.cartRowDelete}>
                                    <Text style={styles.cartRowDeleteText}>🗑</Text>
                                </TouchableOpacity>
                            </HStack>
                            {/* 항목 사이 점선(기준 통일). 마지막 뒤에는 안 넣음 */}
                            {idx < cart.items.length - 1 ? (
                                <DashedLine dashLength={3} dashGap={3} dashThickness={1} dashColor={PALETTE.DIVIDER} style={{height: 1}} />
                            ) : null}
                        </React.Fragment>
                    ))}
                </ScrollView>
                <ScrollMoreHint visible={showHint} onPress={scrollToEnd}/>
                </View>
            )}
            <View style={styles.cartPanelFoot}>
                <HStack style={styles.cartSubRow}>
                    <Text style={styles.cartSubLabel}>Subtotal</Text>
                    <Text style={styles.cartSubValue}>{money(cart.total())}</Text>
                </HStack>
                <TouchableOpacity
                    style={[styles.cartCheckoutBtn, { backgroundColor: accent }, isEmpty && styles.cartCheckoutBtnDisabled]}
                    disabled={isEmpty}
                    // 데스크탑 3-pane에서는 우측 패널이 이미 카트를 보여주므로, Cart 모달을 다시 열 필요 없이
                    // 곧장 Checkout(= "Order Details") 화면으로 이동. (모바일은 FAB→Cart 모달→Checkout 유지)
                    onPress={() => navigation.navigate("Checkout")}
                >
                    <Text style={[styles.cartCheckoutText, { color: onAccent }]}>Go To Checkout</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
}

function MenuList({ cateMenuList, shopid, saleMode, onClick, wide }: {
    cateMenuList: CateMenuListData[];
    shopid: string;
    saleMode: SaleMode;
    onClick: (m: MenuListData) => void;
    wide: boolean;   // 3-pane 여부(부모 Shop이 계산해 전달) — FAB와 단일 소스 공유
}) {
    const design = apps.getDesign();
    const { width } = useWindowDimensions();
    const cols = columnsFor(width, design);
    const sections: MenuSection[] = React.useMemo(() => buildSections(cateMenuList, cols), [cateMenuList, cols]);

    // Fixed heights are REQUIRED for an exact getItemLayout. The menu is flattened
    // into a single [header, row, row, header, ...] array so the FlatList index is
    // 1:1 with the array (no hidden SectionList footer slot), which makes offsets a
    // trivially-exact prefix sum shared by BOTH tap-scroll and the scroll-spy.
    // Fixed row heights sized to the D2 mockup card's WORST case (2-line name +
    // flag pills + 2-line desc + price row) so overflow:"hidden" never clips the
    // bottom price row. ROW_H is the single source feeding flattenWithOffsets,
    // getItemLayout AND the wrapper <View> height below, so tap-scroll/scroll-spy
    // stay exact regardless of the value. CARDS varies by thumb height (=cols).
    const HEADER_H = 52;   // 40→52: 헤더 위 여백↑(이전 카테고리 마지막 메뉴와 간격). sectionBox flex-end라 늘어난 높이가 위로.
    // ROW_H = wrapper 높이(getItemLayout·scroll-spy의 유일 소스). renderMenuCard와 동일한
    // cardsThumbHFor·ROW_BASE(=INFO_H 160 + ROW_GAP 12 = 172)를 공유해 카드 고정높이(INFO_H)와
    // 정확히 맞물린다(B1). 카드 정보영역이 고정 INFO_H라 flag/desc 유무와 무관하게 clip 없음.
    const cardsThumbH = cardsThumbHFor(cols); // must match renderMenuCard (shared)
    const ROW_H = design === "CARDS" ? cardsThumbH + ROW_BASE : ROW_BASE;
    const layout = React.useMemo<FlatLayout>(() => flattenWithOffsets(sections, HEADER_H, ROW_H), [sections, ROW_H]);
    const layoutRef = React.useRef(layout);
    layoutRef.current = layout;

    const listRef = React.useRef<FlatList<FlatEntry>>(null);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [showAllCats, setShowAllCats] = React.useState(false); // B4: 좌측 전체 카테고리 패널
    const tabsRef = React.useRef<FlatList<MenuSection>>(null);
    const activeIndexRef = React.useRef(activeIndex);
    activeIndexRef.current = activeIndex;
    const [alcoholWarn, setAlcoholWarn] = React.useState(false);   // 주류 경고 모달 표시
    const pendingAlcoholMenu = React.useRef<MenuListData | null>(null);   // 경고 확인 후 열 메뉴
    // 주류 카테고리에 속한 메뉴 id 집합. 탭이 아니라 스크롤로 접근해도 커버하려고 메뉴 탭 시점에 판정.
    const alcoholMenuIds = React.useMemo(
        () => new Set(cateMenuList.filter((c) => c.alcohol).flatMap((c) => c.menus.map((m) => m.id))),
        [cateMenuList],
    );

    // Tap a category chip/sidebar item: jump the list to that section's header
    // offset (exact, deterministic). animated:false keeps the spy from fighting
    // the jump, so no suppression flag is needed.
    const onTapTab = (i: number) => {
        setActiveIndex(i);
        tabsRef.current?.scrollToIndex({ index: i, viewPosition: 0.5, animated: true });
        listRef.current?.scrollToOffset({ offset: layout.sectionOffset[i] ?? 0, animated: false });
        // 주류 카테고리 탭 시에도 세션 1회 경고(확인 후 열 메뉴는 없음).
        if (sections[i]?.alcohol && !apps.alcoholAcknowledged && apps.currentShop?.state) {
            pendingAlcoholMenu.current = null;
            setAlcoholWarn(true);
        }
    };

    // 메뉴 카드 탭: 주류 메뉴를 세션 첫 탭하면 경고를 먼저 띄우고(확인 후 상세 오픈), 그 외엔 바로 상세.
    const onClickGuarded = (m: MenuListData) => {
        if (alcoholMenuIds.has(m.id) && !apps.alcoholAcknowledged && apps.currentShop?.state) {
            pendingAlcoholMenu.current = m;
            setAlcoholWarn(true);
        } else {
            onClick(m);
        }
    };

    const closeAlcoholWarn = () => {
        apps.alcoholAcknowledged = true;
        setAlcoholWarn(false);
        const pending = pendingAlcoholMenu.current;
        pendingAlcoholMenu.current = null;
        if (pending) onClick(pending);   // 확인 후 탭했던 메뉴 상세 오픈
    };

    const alcoholEl = (alcoholWarn && apps.currentShop?.state) ? (
        <AlcoholWarning uri={alcoholWarningUri(apps.currentShop.state)} onClose={closeAlcoholWarn} />
    ) : null;

    // Reverse scroll-spy (B5): scrolling the menu drives the active chip. Uses the
    // SAME prefix-sum model as the tap, so the two directions cannot desync. +1 so
    // a header counts as active the instant its top crosses the viewport top.
    const onScroll = (e: any) => {
        const y = e.nativeEvent.contentOffset.y;
        const idx = sectionIndexForOffset(layoutRef.current.sectionOffset, y + 1);
        if (idx >= 0 && idx !== activeIndexRef.current) {
            setActiveIndex(idx);
            tabsRef.current?.scrollToIndex({ index: idx, viewPosition: 0.5, animated: true });
        }
    };

    // Desktop 3-pane gate (B7): `wide`는 부모 Shop이 계산해 전달(FAB와 동일 소스).
    // Below 1100 the layout is unchanged (horizontal tabs + list + mobile FAB).

    // Build the FlatList element ONCE and reuse it in both layouts, so the two
    // branches can never drift. Each cell is wrapped in a PLAIN RN <View> with an
    // explicit height (native-base style height does not reliably clamp) and zero
    // margin, so the real DOM position matches getItemLayout exactly.
    const listEl = (
        <FlatList
            ref={listRef}
            data={layout.entries}
            keyExtractor={(e) => e.key}
            renderItem={({ item }) =>
                item.type === "header" ? (
                    <View style={[styles.sectionBox, { height: HEADER_H, marginTop: 0, overflow: "hidden" }]}>
                        <Text style={styles.sectionText}>{item.cateName}</Text>
                    </View>
                ) : (
                    <View style={{ height: ROW_H, overflow: "hidden" }}>
                        <HStack px={cols > 1 ? "12px" : "0px"} style={styles.menuRow}>
                            {item.row.menus.map((m) => (
                                <Box key={m.id} flex={1} mx={cols > 1 ? "6px" : "0px"}>
                                    {renderMenuCard(m, saleMode, shopid, onClickGuarded, design, cols)}
                                </Box>
                            ))}
                            {item.row.menus.length < cols &&
                                Array.from({ length: cols - item.row.menus.length }).map((_, k) => (
                                    <Box key={`pad-${k}`} flex={1} mx="6px" />
                                ))}
                        </HStack>
                    </View>
                )
            }
            getItemLayout={(_data, index) => ({
                length: layout.entries[index]?.type === "header" ? HEADER_H : ROW_H,
                offset: layout.offsets[index] ?? 0,
                index,
            })}
            onScroll={onScroll}
            scrollEventThrottle={16}
            initialNumToRender={12}
            windowSize={11}
            style={{ flex: 1, width: "100%", minHeight: 0 }}
        />
    );

    if (wide) {
        // 3-pane: sidebar replaces the horizontal tab strip; cart panel replaces the FAB.
        return (
            <View style={styles.paneRow}>
                <CategorySidebar sections={sections} activeIndex={activeIndex} onTap={onTapTab} />
                <View style={styles.paneCenter}>{listEl}</View>
                <CartPanel />
                {alcoholEl}
            </View>
        );
    }

    return (
        <View style={{ flex: 1, width: "100%", minHeight: 0 }}>
            <View style={styles.tabsRow}>
                <TouchableOpacity
                    onPress={() => setShowAllCats(true)}
                    style={styles.allCatBtn}
                    accessibilityLabel="All categories"
                >
                    <Text style={styles.allCatBtnGlyph}>☰</Text>
                </TouchableOpacity>
                <View style={{ flex: 1, minWidth: 0 }}>
                    <CategoryTabs sections={sections} activeIndex={activeIndex} onTap={onTapTab} tabsRef={tabsRef} />
                </View>
            </View>
            {listEl}
            <AllCategoriesPanel
                sections={sections}
                activeIndex={activeIndex}
                onTapTab={onTapTab}
                visible={showAllCats}
                onClose={() => setShowAllCats(false)}
            />
            {alcoholEl}
        </View>
    );
}

function TableNo() {
    const yourOrder = useRecoilValue(orderState);
    const navigation = useNavigation<any>();
    const accent = apps.getAccent();

    return (
        <VStack style={styles.tableNoContainer}>
            <View style={styles.tableNoRow}>
                {/* 좌측 스페이서: 우측 검색 버튼과 대칭 → 박스 중앙 유지 */}
                <View style={styles.tableNoSearchBtn} />
                <Box style={styles.tableNoBox}>
                    <Text style={styles.tableNo}>{yourOrder.getOrderInfoLong()}</Text>
                </Box>
                <TouchableOpacity
                    style={styles.tableNoSearchBtn}
                    onPress={() => navigation.navigate("Search")}
                    accessibilityLabel="Search menu"
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                    <Icon as={Ionicons} name="search-outline" size={6} color={accent} />
                </TouchableOpacity>
            </View>
            {(apps.isTakeaway() && apps.currentShop.descr.length > 0) && <Box>
                <Text style={styles.shopDescr}>
                    {apps.currentShop.descr}
                </Text>
            </Box>}
        </VStack>
    );
}

function ViewCartPopup() {
    const cart = useRecoilValue(cartState);
    const navigation = useNavigation<any>();
    const { width } = useWindowDimensions();
    const [isCartView, setIsCartView] = useState(false);

    useEffect(() => {
        if (isCartView) {
            navigation.navigate("Cart");
        }
        return () => {
            setIsCartView(false);
        };
    }, [isCartView]);

    if (cart.count == 0) return null;

    const accent = apps.getAccent();
    const onAccent = apps.onAccent();

    // 태블릿(768~1100)에서는 FAB를 더 크게. 위치는 %가 아니라 우하단 고정 여백으로 앵커링해
    // 화면 폭에 상관없이 항상 모서리에서 일정 간격에 뜨게 한다(넓은 태블릿서 붕 뜨는 문제 해결).
    const isTablet = width > 768;
    const size = isTablet ? 84 : 66;
    const margin = isTablet ? 32 : 20;
    const iconSize = isTablet ? 12 : 9;   // native-base 사이즈 토큰(9=36px, 12=48px) — 11은 유효 토큰 아님

    return (
        <TouchableWithoutFeedback onPress={() => { setIsCartView(true); }}>
            <Box style={[styles.viewCartPopup, {
                width: size, height: size, borderRadius: size * 0.3,
                right: margin, bottom: margin,
                backgroundColor: accent, borderColor: accent,
            }]}>
                <View style={styles.cartIconWrap} pointerEvents="none">
                    <Icon as={Ionicons} name="cart-outline" size={iconSize} color={onAccent} />
                    <View style={[styles.cartBadge, { borderColor: accent }]}>
                        <Text style={[styles.cartBadgeText, { color: accent }]}>{cart.totalQty()}</Text>
                    </View>
                </View>
            </Box>
        </TouchableWithoutFeedback>
    );
}

function HappyHour() {
    const happyhour: Happyhour = apps.getHappyhour();
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();

    return (
        <View style={[styles.happyhourBack, { backgroundColor: accent }]}>
            <Text style={[styles.happyhourText, { color: onAccent }]}>{apps.getHappyhourDescr(happyhour.saleMode)}</Text>
            <Text style={[styles.happyhourSubtext, { color: onAccent }]}>{apps.getHappyhourTime(happyhour)}</Text>
        </View>
    );
}

function Shop({ navigation }) {
    const { width } = useWindowDimensions();
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    const currentShop = apps.currentShop;
    const [showNotice, setShowNotice] = useState(false);

    useEffect(() => {
        if (currentShop.shopNotice) {
            setShowNotice(true);
        }
    }, [currentShop]);

    const { isLoading, error, data } = useQuery<CateMenuAndOptionData>(["cateMenuAndOption"], () => remote.getCateMenuAndOptionList(currentShop.id, apps.tableNo));

    if (error) return (<Text>"An error has occurred: " + error.message</Text>);

    if (data) {
        apps.options = data.options;
    }

    const menuSelected = (m: MenuListData) => {
        apps.selectedMenu = m;
        navigation.navigate("Menu");
    };

    const saleMode = apps.getHappyhour().saleMode;
    apps.saleMode = saleMode;

    const isDesktop = width > 768;
    // 오른쪽 CartPanel(>=1100)과 FAB(<1100)의 단일 소스. 같은 boolean을 MenuList로 넘겨
    // 패널과 FAB가 절대 동시에 뜨지 않도록 한다(각 컴포넌트가 width를 따로 읽던 방식 → 게이트 불일치 방지).
    // wide 전환에 150ms 디바운스(히스테리시스): transparentModal 여닫을 때 visualViewport 폭이
    // 1100 경계에서 순간(~16ms) 튀어 wide가 뒤집히는 것을 흡수한다. 진짜 리사이즈만 반영(초기값은 즉시).
    const wideRef = React.useRef(width >= 1100);
    const [wide, setWide] = useState(width >= 1100);
    useEffect(() => {
        const next = width >= 1100;
        if (next === wideRef.current) return;   // 경계 안 넘음 → 무시
        const t = setTimeout(() => { wideRef.current = next; setWide(next); }, 150);
        return () => clearTimeout(t);           // 150ms 안에 또 바뀌면 예약 취소(튐 흡수)
    }, [width]);
    const containerStyle = isDesktop ? styles.containerDesktop : styles.containerMobile;

    return (
        <View style={containerStyle}>
            <TitleBar shop={apps.currentShop} />
            {saleMode != SaleMode.NORMAL && <HappyHour />}
            <TableNo />
            {isLoading && LoadingIndicator({ marginTop: 200 })}
            {data && <MenuList cateMenuList={data.cateMenus} shopid={currentShop.shopid} saleMode={saleMode} onClick={menuSelected} wide={wide} />}
            {/* wide(>=1100)면 오른쪽 CartPanel이 FAB를 대체 — 같은 wide로 게이팅 */}
            {!wide && <ViewCartPopup />}
            {showNotice && (
                <Modal
                    transparent={true}
                    animationType="slide"
                    visible={showNotice}
                    onRequestClose={() => setShowNotice(false)}
                >
                    <View style={styles.modalBackground}>
                        <View style={styles.modalContainer}>
                            {/* 상단 accent 아이콘 배지(component.gallery Dialog·mobbin 다이얼로그 패턴). accentTint 원+accent 벨. */}
                            <View style={[styles.modalIconWrap, { backgroundColor: apps.accentTint() }]}>
                                <Icon as={Ionicons} name="notifications-outline" size={7} color={accent} />
                            </View>
                            <Text style={styles.modalTitle}>Notice</Text>
                            <Text style={styles.modalMessage}>{currentShop.shopNoticeDescr}</Text>
                            <TouchableOpacity
                                style={[styles.modalButton, { backgroundColor: accent }]}
                                onPress={() => setShowNotice(false)}
                            >
                                <Text style={[styles.modalButtonText, { color: onAccent }]}>Got it</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Modal>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    containerMobile: {
        flex: 1,
        minHeight: 0,
        backgroundColor: PALETTE.BG,
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    containerDesktop: {
        flex: 1,
        backgroundColor: PALETTE.BG,
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginHorizontal: '0%',  // Updated to 0%
    },
    titleContainer: {
        width: "100%",
        height: 56,
        paddingLeft: 20,
        paddingRight: 20,
        backgroundColor: PALETTE.SURFACE,
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
        justifyContent: "space-between",
        alignItems: "center",
    },
    shopName: {
        fontFamily: "NotoSans-Bold",
        fontSize: 22,
        fontWeight: "900",
        color: PALETTE.TEXT,
        letterSpacing: -0.55,
    },
    tabNames: {
        width: "100%",
        backgroundColor: "white",
        marginTop: 0,
        paddingLeft: 20,
        paddingBottom: 15,
        marginRight: 0,
        marginBottom: -5,
    },
    sectionBox: {
        width: "100%",
        height: 42,                 // (실높이는 HEADER_H로 인라인 오버라이드)
        justifyContent: "flex-end", // 헤더 텍스트를 박스 하단에 붙여, 늘어난 높이가 헤더 '위' 여백으로 가게(이전 카테고리와 분리)
        paddingBottom: 8,
        paddingLeft: 16,
    },
    sectionText: {
        fontFamily: "NotoSans-Bold",
        fontSize: 17,
        fontWeight: "800",
        color: PALETTE.HEAD,
        letterSpacing: -0.1,
    },
    cateAndMenu: {
        borderTopWidth: 1,
        borderColor: PALETTE.BORDER,
    },
    empty: {},
    tableNoContainer: {
        width: "100%",
        paddingVertical: 9,
        backgroundColor: PALETTE.SUBTLE,
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
        alignItems: "center",
        justifyContent: "center",
    },
    tableNoBox: {
        backgroundColor: PALETTE.SURFACE,
        borderWidth: 1,
        borderRadius: 10,
        borderColor: PALETTE.BORDER,
        paddingVertical: 6,
        paddingHorizontal: 16,
        alignItems: "center",
        justifyContent: "center",
    },
    tableNoRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        paddingHorizontal: 16,
    },
    tableNoSearchBtn: {
        width: 34, height: 34,
        alignItems: "center",
        justifyContent: "center",
    },
    tableNo: {
        fontFamily: "NotoSans-Regular",
        fontSize: 12.5,
        fontWeight: "700",
        color: PALETTE.MUTED,
    },
    happyhourBack: {
        width: "100%",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        paddingVertical: 9,
        paddingHorizontal: 14,
    },
    happyhourText: {
        fontFamily: "NotoSans-Bold",
        fontWeight: "800",
        fontSize: 13,
        letterSpacing: 0.3,
    },
    happyhourSubtext: {
        fontFamily: "NotoSans-Regular",
        fontSize: 11.5,
        opacity: 0.92,
    },
    shopDescr: {
        fontSize: 14,
        letterSpacing: -0.3,
        alignItems: "center",
        textAlign: "left",
        marginLeft: 30,
        marginRight: 30,
        marginTop: 3,
        marginBottom: 5,
        color: PALETTE.MUTED,
    },
    customSectionProps: {
        width: "100%",
        padding: 0,
    },
    viewCartPopup: {
        backgroundColor: PALETTE.ACCENT,
        width: 66,          // 기본값(폴백) — 실제 크기/위치는 ViewCartPopup에서 반응형 인라인으로 지정
        height: 66,
        position: "absolute",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 20,
        shadowOffset: { width: 2, height: 4 },
        shadowColor: "#00000070",
        shadowOpacity: 0.8,
        borderWidth: 1,
        borderColor: "#e36ca1",
    },
    iconBag: {
        width: 38,
        height: 30,
        paddingTop: 7,
        alignItems: "center",
        justifyContent: "center",
    },
    cartIconWrap: {
        position: "relative", alignItems: "center", justifyContent: "center",
        marginLeft: -10,  // 카트 손잡이(좌측 돌출) 보정 — 몸통을 가운데로, 좀 더 왼쪽
        marginTop: 4,     // 조금 더 아래로
    },
    cartBadge: {
        position: "absolute",
        top: -3, right: -9,   // 카트 우측 상단 바깥에 배지
        minWidth: 18, height: 18,
        borderRadius: 9,
        paddingHorizontal: 4,
        backgroundColor: "#fff",
        borderWidth: 1,   // borderColor는 accent 인라인
        alignItems: "center", justifyContent: "center",
    },
    cartBadgeText: {
        fontSize: 11,
        fontWeight: "900",
        letterSpacing: -0.3,
    },
    modalBackground: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(20, 24, 32, 0.45)',
    },
    modalContainer: {
        width: '85%',
        maxWidth: 400,
        backgroundColor: PALETTE.SURFACE,
        paddingHorizontal: 22,
        paddingTop: 22,
        paddingBottom: 18,
        borderRadius: PALETTE.CARD_RADIUS,
        borderWidth: 1,
        borderColor: PALETTE.BORDER,
        alignItems: 'stretch',   // 버튼 풀폭
        ...PALETTE.CARD_SHADOW,
    },
    modalIconWrap: {
        width: 56, height: 56, borderRadius: 28,
        alignItems: 'center', justifyContent: 'center',
        alignSelf: 'center',
        marginBottom: 14,
    },
    modalTitle: {
        fontSize: 17,
        fontWeight: '800',
        marginBottom: 10,
        color: PALETTE.TEXT,
        textAlign: 'center',
        letterSpacing: -0.2,
    },
    modalMessage: {
        fontSize: 14,
        lineHeight: 21,
        textAlign: 'center',
        marginBottom: 22,
        color: PALETTE.MUTED,
    },
    modalButton: {
        paddingVertical: 13,
        borderRadius: PALETTE.RADIUS_MD,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#db146d', // inert fallback; overridden at render time by {backgroundColor: accent}
    },
    modalButtonText: {
        fontSize: 15,
        fontWeight: '800',
        color: 'white', // inert fallback; overridden at render time by {color: onAccent}
    },
    // B4: category tab strip
    tabsStrip: {
        width: "100%",
        flexGrow: 0,
        flexShrink: 0,
        paddingVertical: 11,
        backgroundColor: PALETTE.SURFACE,
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
    },
    tabsContent: {
        paddingHorizontal: 8,
    },
    chip: {
        marginRight: 8,
        paddingHorizontal: 15,
        paddingVertical: 8,
        borderRadius: PALETTE.RADIUS_PILL,
        alignItems: "center",
        justifyContent: "center",
    },
    chipText: {
        fontSize: 14,
        fontWeight: "700",
    },
    // B4: all-categories button (탭 스트립 앞) + 좌측 슬라이드 패널
    tabsRow: {
        flexDirection: "row",
        alignItems: "stretch",
        width: "100%",
        backgroundColor: PALETTE.SURFACE,
    },
    allCatBtn: {
        paddingHorizontal: 14,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: PALETTE.SURFACE,
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
        borderRightWidth: 1,
        borderRightColor: PALETTE.BORDER,
    },
    allCatBtnGlyph: {
        fontSize: 20,
        fontWeight: "700",
        color: PALETTE.TEXT,
        lineHeight: 22,
    },
    allCatBackdrop: {
        flex: 1,
        backgroundColor: "rgba(20,24,32,0.45)",
    },
    allCatPanel: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        backgroundColor: PALETTE.SURFACE,
        borderRightWidth: 1,
        borderRightColor: PALETTE.BORDER,
        paddingTop: 16,
        ...PALETTE.CARD_SHADOW,
    },
    allCatHead: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 16,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
    },
    allCatTitle: {
        fontSize: 16,
        fontWeight: "800",
        color: PALETTE.TEXT,
    },
    allCatCloseGlyph: {
        fontSize: 14,
        fontWeight: "700",
        color: "#ffffff",
        lineHeight: 16,
    },
    allCatItem: {
        paddingHorizontal: 16,
        paddingVertical: 13,
        marginHorizontal: 8,
        marginTop: 4,
        borderRadius: 11,
    },
    allCatItemText: {
        fontSize: 15,
        fontWeight: "700",
        color: PALETTE.TEXT,
        letterSpacing: -0.3,
    },
    allCatItemTextUnselected: {
        color: PALETTE.TEXT,
    },
    // B6a: 우측 Store Info 슬라이드 시트
    storeBackdrop: {
        flex: 1,
        backgroundColor: "rgba(20,24,32,0.45)",
    },
    storePanel: {
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        backgroundColor: PALETTE.SURFACE,
        borderLeftWidth: 1,
        borderLeftColor: PALETTE.BORDER,
        paddingTop: 16,
        ...PALETTE.CARD_SHADOW,
    },
    storeHead: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 16,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
    },
    storeTitle: {
        fontSize: 16,
        fontWeight: "800",
        color: PALETTE.TEXT,
    },
    storeCloseGlyph: {
        fontSize: 14,
        fontWeight: "700",
        color: "#ffffff",
        lineHeight: 16,
    },
    storeName: {
        fontSize: 17,
        fontWeight: "800",
        color: PALETTE.TEXT,
        letterSpacing: -0.3,
    },
    storeAddr: {
        fontSize: 13,
        color: PALETTE.MUTED,
        marginTop: 4,
        lineHeight: 18,
    },
    mapWrap: {
        marginTop: 10,
        borderRadius: 12,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: PALETTE.BORDER,
    },
    directionsBtn: {
        marginTop: 10,
        alignItems: "center",
        paddingVertical: 10,
        borderRadius: PALETTE.RADIUS_PILL,
        borderWidth: 1.5,
    },
    directionsBtnText: {
        fontSize: 14,
        fontWeight: "800",
        letterSpacing: -0.2,
    },
    storeSectionLabel: {
        fontSize: 11,
        fontWeight: "800",
        color: PALETTE.MUTED,
        letterSpacing: 0.7,
        textTransform: "uppercase",
        marginTop: 20,
        marginBottom: 8,
    },
    storeHoursRow: {
        justifyContent: "space-between",
        alignItems: "center",   // 요일 라벨 · 시간/브레이크 컬럼 모두 세로 중앙정렬
        minHeight: 46,          // 2줄분 고정 → break 유무와 무관하게 행 높이 동일 + 내용 가운데
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
    },
    storeDay: {
        fontSize: 13,
        fontWeight: "700",
        color: PALETTE.TEXT,
    },
    storeHoursCol: {
        flexShrink: 1,
        marginLeft: 12,
        alignItems: "flex-end",
    },
    storeHoursText: {
        fontSize: 12,
        lineHeight: 17,
        color: PALETTE.TEXT,
        textAlign: "right",
    },
    storeBreakText: {
        fontSize: 11,
        lineHeight: 15,
        color: PALETTE.MUTED,
        textAlign: "right",
    },
    storeHoursHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 20,     // storeSectionLabel의 세로 마진을 헤더 행으로 이관(라벨과 Today 수평 정렬)
        marginBottom: 8,
    },
    storeHoursHeaderLabel: {
        marginTop: 0,
        marginBottom: 0,
    },
    storeHoursHint: {
        fontSize: 11,
        fontWeight: "700",
        color: PALETTE.MUTED,
        marginRight: 2,
    },
    storeDayToday: {
        fontWeight: "900",
    },
    storeHoursTextToday: {
        fontWeight: "800",
    },
    callStaffBtn: {
        paddingVertical: 13,
        borderRadius: PALETTE.RADIUS_MD,
        alignItems: "center",
        justifyContent: "center",
    },
    callStaffCancel: {
        backgroundColor: PALETTE.BORDER,
    },
    callStaffBtnText: {
        fontSize: 15,
        fontWeight: "800",
    },
    callStaffPrompt: {
        fontSize: 14,
        color: PALETTE.TEXT,
        textAlign: "center",
        marginTop: 4,
    },
    // B3: row container for multi-column grid
    menuRow: {
        alignItems: "stretch",
    },
    // B7: desktop 3-pane layout
    paneRow: {
        flex: 1,
        width: "100%",
        flexDirection: "row",
    },
    paneCenter: {
        flex: 1,
        minWidth: 0,
    },
    // B7: left category sidebar (replaces the horizontal tab strip at desktop)
    sidebar: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 232,
        backgroundColor: PALETTE.SUBTLE,
        borderRightWidth: 1,
        borderRightColor: PALETTE.BORDER,
    },
    sidebarContent: {
        padding: 12,
    },
    sidebarTitle: {
        fontFamily: "NotoSans-Bold",
        fontSize: 11,
        fontWeight: "800",
        color: PALETTE.MUTED,
        letterSpacing: 0.7,
        textTransform: "uppercase",
        paddingHorizontal: 12,
        paddingTop: 6,
        paddingBottom: 10,
    },
    sidebarItem: {
        paddingHorizontal: 13,
        paddingVertical: 11,
        borderRadius: 11,
        marginBottom: 3,
    },
    sidebarItemText: {
        fontFamily: "NotoSans-Bold",
        fontSize: 15,
        fontWeight: "700",
        letterSpacing: -0.3,
    },
    sidebarItemTextUnselected: {
        color: PALETTE.MUTED,
    },
    // B7: right cart panel (replaces the mobile FAB at desktop)
    cartPanel: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 300,
        backgroundColor: PALETTE.SUBTLE,
        borderLeftWidth: 1,
        borderLeftColor: PALETTE.BORDER,
    },
    cartPanelHead: {
        paddingHorizontal: 18,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
    },
    cartPanelTitle: {
        fontFamily: "NotoSans-Bold",
        fontSize: 16,
        fontWeight: "800",
        color: PALETTE.TEXT,
    },
    cartPanelHint: {
        fontFamily: "NotoSans-Regular",
        fontSize: 11,
        color: PALETTE.MUTED,
        marginTop: 4,
    },
    cartPanelItems: {
        flex: 1,
        paddingVertical: 8,
    },
    cartRow: {
        paddingHorizontal: 18,
        paddingVertical: 11,
        alignItems: "center",
    },
    cartRowTap: {
        flex: 1,
    },
    cartRowOptions: {
        fontFamily: "NotoSans-Regular",
        fontSize: 11,
        color: PALETTE.MUTED,
        lineHeight: 15,
        marginTop: 2,
    },
    cartRowNote: {
        fontFamily: "NotoSans-Regular",
        fontSize: 11,
        fontStyle: "italic",
        color: PALETTE.MUTED,
        lineHeight: 15,
        marginTop: 2,
    },
    cartRowDelete: {
        width: 28, height: 28,
        borderRadius: PALETTE.RADIUS_SM,
        backgroundColor: PALETTE.CHIP_BG,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 8,
    },
    cartRowDeleteText: {
        fontSize: 13,
        fontFamily: "NotoSans-Regular",
    },
    cartRowThumb: {
        width: 34, height: 34, borderRadius: PALETTE.RADIUS_SM, marginLeft: 4, marginRight: 8, backgroundColor: PALETTE.BORDER,
    },
    cartRowRight: {
        alignItems: "flex-end",
    },
    cartRowQtyChip: {
        marginTop: 4, borderRadius: PALETTE.RADIUS_PILL, paddingHorizontal: 8, paddingVertical: 1, backgroundColor: PALETTE.QTY_CHIP,
    },
    cartRowQtyChipText: {
        fontSize: 11, fontWeight: "700", color: "#FFFFFF", letterSpacing: -0.2,
    },
    cartQty: {
        fontFamily: "NotoSans-Bold",
        fontSize: 12,
        fontWeight: "800",
        borderRadius: 7,
        paddingHorizontal: 7,
        paddingVertical: 2,
        marginRight: 10,
        overflow: "hidden",
    },
    cartRowMain: {
        flex: 1,
    },
    cartRowName: {
        fontFamily: "NotoSans-Regular",
        fontSize: 13,
        color: PALETTE.TEXT,
        lineHeight: 18,
    },
    cartRowPrice: {
        fontFamily: "NotoSans-Bold",
        fontSize: 13,
        fontWeight: "800",
        color: PALETTE.TEXT,
        marginLeft: 8,
    },
    cartPanelEmpty: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 18,
    },
    cartPanelEmptyText: {
        fontFamily: "NotoSans-Regular",
        fontSize: 13,
        color: PALETTE.MUTED,
    },
    cartPanelFoot: {
        borderTopWidth: 1,
        borderTopColor: PALETTE.DIVIDER,
        paddingHorizontal: 18,
        paddingVertical: 16,
    },
    cartSubRow: {
        justifyContent: "space-between",
        marginBottom: 14,
    },
    cartSubLabel: {
        fontFamily: "NotoSans-Regular",
        fontSize: 13,
        color: PALETTE.MUTED,
    },
    cartSubValue: {
        fontFamily: "NotoSans-Bold",
        fontSize: 17,
        fontWeight: "800",
        color: PALETTE.TEXT,
    },
    cartCheckoutBtn: {
        borderRadius: PALETTE.RADIUS_MD,
        paddingVertical: 14,
        alignItems: "center",
        justifyContent: "center",
    },
    cartCheckoutBtnDisabled: {
        opacity: 0.4,
    },
    cartCheckoutText: {
        fontFamily: "NotoSans-Bold",
        fontSize: 15,
        fontWeight: "800",
    },

});

export default Shop;
